import { useState, useRef } from 'react';
import Toast from 'react-native-toast-message';
import { useSelector } from 'react-redux';
import { MsAccount } from '@api/ms-account';

export const MENTION_TOKEN_PATTERN = /@([A-Za-z]+(?:\s[A-Za-z]*){0,2})$/;
const MENTION_PAGE_SIZE = 10;
const MENTION_MIN_CHARS = 3;
const MENTION_MAX_COUNT = 100;
// Roles that can never be @mentioned. Mirrors NON_MENTIONABLE_ROLES in
// b2b-api/app/controllers/comments.js, which also drops them server-side.
const NON_MENTIONABLE_ROLES = ['3pl'];

// Shared between CommentInput (new comment) and MessageItem (edit comment) — same "@" + 3
// chars user search, MentionDropdown keyboard navigation and mention bookkeeping. The caller
// owns the text value (content/setContent) and the TextInput ref; onCursorChange lets a caller
// that controls `selection` keep it in sync after a mention is inserted.
export default function useCommentMentions({
  content,
  setContent,
  inputRef,
  initialMentions = [],
  onCursorChange,
}) {
  const currentUser = useSelector((state) => state.auth.user);
  const [mentionUsers, setMentionUsers] = useState([]);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionSkip, setMentionSkip] = useState(0);
  const [mentionTotal, setMentionTotal] = useState(0);
  const [mentions, setMentions] = useState(initialMentions);
  const [activeMentionIndex, setActiveMentionIndex] = useState(-1);
  const debounceRef = useRef(null);

  const buildMentionFilter = (query) => {
    const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return {
      name: { $regex: `\\b${escaped}`, $options: 'i' },
      is_active: true,
      is_active_for: currentUser?.is_active_for,
      'role.code': { $nin: NON_MENTIONABLE_ROLES },
    };
  };

  const fetchMentionUsers = async (query, skip, append) => {
    try {
      const filter = buildMentionFilter(query);
      const requests = [
        MsAccount.getUsers({
          params: { filter, skip, limit: MENTION_PAGE_SIZE },
        }),
      ];
      // Only need the total count once, on the first page of a given search.
      if (skip === 0) {
        requests.push(MsAccount.getUsersCount({ params: { filter } }));
      }
      const [usersResponse, countResponse] = await Promise.all(requests);
      const data = usersResponse?.data?.data || [];
      setMentionUsers((prev) => (append ? [...prev, ...data] : data));
      if (!append) {
        setActiveMentionIndex(data.length ? 0 : -1);
      }
      setMentionSkip(skip);
      if (countResponse) {
        setMentionTotal(countResponse?.data?.data || 0);
      }
    } catch (e) {
      if (!append) {
        setMentionUsers([]);
        setActiveMentionIndex(-1);
      }
    }
  };

  const searchMentions = (query) => {
    clearTimeout(debounceRef.current);
    setMentionQuery(query);
    debounceRef.current = setTimeout(() => {
      fetchMentionUsers(query, 0, false);
    }, 300);
  };

  const onLoadMoreMentions = () => {
    fetchMentionUsers(mentionQuery, mentionSkip + MENTION_PAGE_SIZE, true);
  };

  const mentionHasMore = mentionUsers.length < mentionTotal;

  const clearMentionUsers = () => setMentionUsers([]);

  // Call from the TextInput's onChangeText, after updating the text value.
  const onMentionTextChange = (value) => {
    const match = value.match(MENTION_TOKEN_PATTERN);
    if (match && match[1].length >= MENTION_MIN_CHARS) {
      searchMentions(match[1].trim());
    } else {
      setMentionUsers([]);
    }
  };

  const onSelectMention = (user) => {
    const alreadyMentioned = mentions.some((m) => m.user_id === user._id);
    if (!alreadyMentioned && mentions.length >= MENTION_MAX_COUNT) {
      Toast.show({
        text1: `You can only mention up to ${MENTION_MAX_COUNT} people in one comment`,
        type: 'error',
      });
      setMentionUsers([]);
      return;
    }
    const newContent = content.replace(MENTION_TOKEN_PATTERN, `@${user.name} `);
    setContent(newContent);
    setMentions((prev) => [
      ...prev.filter((m) => m.user_id !== user._id),
      { user_id: user._id, user_name: user.name },
    ]);
    setMentionUsers([]);
    const cursorPos = newContent.length;
    onCursorChange?.(cursorPos);
    requestAnimationFrame(() => {
      const node = inputRef.current;
      if (!node) {
        return;
      }
      if (typeof node.setSelectionRange === 'function') {
        node.setSelectionRange(cursorPos, cursorPos);
      } else if (typeof node.setNativeProps === 'function') {
        node.setNativeProps({
          selection: { start: cursorPos, end: cursorPos },
        });
      }
      node.focus?.();
    });
  };

  const onMentionKeyPress = (e) => {
    if (!mentionUsers.length) {
      return;
    }
    const key = e.nativeEvent.key;
    if (key === 'ArrowDown') {
      e.preventDefault?.();
      e.nativeEvent?.preventDefault?.();
      setActiveMentionIndex((prev) => {
        const next = Math.min(prev + 1, mentionUsers.length - 1);
        if (next === mentionUsers.length - 1 && mentionHasMore) {
          onLoadMoreMentions();
        }
        return next;
      });
    } else if (key === 'ArrowUp') {
      e.preventDefault?.();
      e.nativeEvent?.preventDefault?.();
      setActiveMentionIndex((prev) => Math.max(prev - 1, 0));
    } else if (key === 'Enter') {
      if (activeMentionIndex >= 0 && mentionUsers[activeMentionIndex]) {
        e.preventDefault?.();
        e.nativeEvent?.preventDefault?.();
        onSelectMention(mentionUsers[activeMentionIndex]);
      }
    } else if (key === 'Escape') {
      setMentionUsers([]);
    }
  };

  // Only the mentions whose "@Name" is still in the text — a user can delete a tag by hand.
  const getUsedMentions = (text) =>
    mentions.filter((m) => text.includes(`@${m.user_name}`));

  const resetMentions = (next = []) => {
    setMentions(next);
    setMentionUsers([]);
  };

  return {
    mentions,
    mentionUsers,
    activeMentionIndex,
    mentionHasMore,
    onLoadMoreMentions,
    onMentionTextChange,
    onMentionKeyPress,
    onSelectMention,
    clearMentionUsers,
    getUsedMentions,
    resetMentions,
  };
}
